import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { TOKEN_REFRESH } from "./login.service";
import { handleAxiosError } from "@/lib/errorHandler";

export const axiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export const conversionInstance = axios.create({
  ...axiosInstance.defaults,
  headers: {
    Accept: "application/json",
    "Content-Type": "multipart/form-data",
  },
});

export const loginInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_IAM_URL,
  maxBodyLength: Infinity,
  maxContentLength: Infinity,
});

let isRefreshing = false;
let refreshSubscribers = [];

// Function to subscribe API calls waiting for refresh
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// Function to notify all subscribers when refresh is complete
const onRefreshed = (token) => {
  for (const callback of refreshSubscribers) {
    callback(token);
  }
  refreshSubscribers = [];
};

const setAuthHeader = (token) => {
  const value = `Bearer ${token}`;
  axiosInstance.defaults.headers.common["Authorization"] = value;
  conversionInstance.defaults.headers.common["Authorization"] = value;
};

const attachAuthInterceptors = (instance) => {
  instance.interceptors.request.use(
    async (config) => {
      let token;
      if (typeof window !== "undefined") {
        token = sessionStorage.getItem("access_token");
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken) => {
            config.headers.Authorization = `Bearer ${newToken}`;
            resolve(config);
          });
        });
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        if (!isRefreshing) {
          isRefreshing = true;
          try {
            const refresh_token = sessionStorage.getItem("refresh_token");
            if (!refresh_token) {
              clearTokenTimer();
              sessionStorage.clear();
              window.location.href = "/";
              return Promise.reject(error);
            }

            const response = await TOKEN_REFRESH(refresh_token);
            if (response.statusCode === 200) {
              sessionStorage.setItem("isLogin", true);
              sessionStorage.setItem("access_token", response?.data?.access_token);
              sessionStorage.setItem("expires_in", response?.data?.expires_in);
              sessionStorage.setItem("refresh_token", response?.data?.refresh_token);
              sessionStorage.setItem("refresh_expires_in", response?.data?.access_token);

              const newToken = response?.data?.access_token;
              setAuthHeader(newToken);
              onRefreshed(newToken);

              // Restart the refresh cycle with the new token
              triggerTokenManager(newToken);

              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            handleAxiosError(refreshError);
            clearTokenTimer();
            sessionStorage.clear();
            handleAxiosError(null,{"customMessage": "Session expired, please log in again."});
            window.location.href = "/";
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }

        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(instance(originalRequest));
          });
        });
      }

      return Promise.reject(error);
    }
  );
};

attachAuthInterceptors(axiosInstance);
attachAuthInterceptors(conversionInstance);

const streamingRequest = (url, method, options = {}) => {
  const token =
    globalThis.window === undefined
      ? null
      : sessionStorage.getItem("access_token");

  const headers = new Headers(options?.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || "";
  const fullUrl = url.startsWith("http") ? url : `${baseUrl}${url}`;

  return fetch(fullUrl, {
    ...options,
    method,
    headers,
  });
};

export const streamingInstance = {
  get: (url, options) => streamingRequest(url, "GET", options),
  post: (url, options) => streamingRequest(url, "POST", options),
};

// Login instance interceptor
loginInstance.interceptors.request.use(
  (config) => {
    let token;
    if (typeof window !== "undefined") {
      token = sessionStorage.getItem("access_token");
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/* Refresh token functionality */
let refreshTimerRef;

export const decodeJWT = (jwt_Token) => {
  try {
    const decodedAccessToken = jwtDecode(jwt_Token);
    const timeToExpiry = decodedAccessToken?.exp - Math.floor(Date.now() / 1000) - 80;
    if (timeToExpiry <= 0) {
      console.warn("Token is expired or about to expire:", timeToExpiry);
      return 5; // Fallback to 5 seconds to trigger immediate refresh
    }
    return timeToExpiry;
  } catch (error) {
    handleAxiosError(error);
    return null;
  }
};

export const triggerTokenManager = (jwt_Token) => {
  clearTokenTimer();
  const function_Timer = decodeJWT(jwt_Token);
  if (function_Timer) {
    refreshTimerRef = setTimeout(refreshToken, function_Timer * 1000);
  } else {
    clearTokenTimer();
    sessionStorage.clear();
    handleAxiosError(null,{"customMessage": "Invalid token, please log in again."});
    window.location.href = "/";
  }
};

const refreshToken = async () => {
  if (isRefreshing) {
    return;
  }
  isRefreshing = true;
  try {
    const refresh_token = sessionStorage.getItem("refresh_token");
    if (!refresh_token) {
      clearTokenTimer();
      sessionStorage.clear();
      window.location.href = "/";
      return;
    }

    const response = await TOKEN_REFRESH(refresh_token);
    if (response.statusCode === 200) {
      sessionStorage.setItem("isLogin", true);
      sessionStorage.setItem("access_token", response?.data?.access_token);
      sessionStorage.setItem("expires_in", response?.data?.expires_in);
      sessionStorage.setItem("refresh_token", response?.data?.refresh_token);
      sessionStorage.setItem("refresh_expires_in", response?.data?.access_token);

      const newToken = response?.data?.access_token;
      setAuthHeader(newToken);
      onRefreshed(newToken);

      // Restart the refresh cycle with the new token
      triggerTokenManager(newToken);
    } else {
      throw new Error("Refresh token failed");
    }
  } catch (error) {
    handleAxiosError(error);
    clearTokenTimer();
    sessionStorage.clear();
    window.location.href = "/";
  } finally {
    isRefreshing = false;
  }
};

export const clearTokenTimer = () => {
  try {
    if (refreshTimerRef) {
      clearTimeout(refreshTimerRef);
      refreshTimerRef = null;
    }
  } catch (error) {
    handleAxiosError(error);
  }
};
